import {
  Autocomplete,
  Button,
  Grid,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useEffect } from "react";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useSearchParams } from "react-router-dom";

interface ClickDataFilterProps {
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  setShow: any;
  setDisplay: any;
  competitorlisting: any;
  setBrandName: any;
  sortedRows: any;
  tableHeadings: any;
  brandName: any;
}

const ClickDataFilter = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  setShow,
  setDisplay,
  competitorlisting,
  setBrandName,
  tableHeadings,
  sortedRows,
  brandName,
}: ClickDataFilterProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const calculateStartAndEndDate = (selectedPeriod: string) => {
    const [month, year] = selectedPeriod.split("/").map(Number);
    const start = dayjs(`${year}-${month}-01`).format("YYYY-MM-DD"); // First day of the month
    const end = dayjs(start).endOf("month").format("YYYY-MM-DD"); // Last day of the month

    setStartDate(start);
    setEndDate(end);
  };

  useEffect(() => {
    const currentMonth = dayjs().format("MM/YYYY");
    calculateStartAndEndDate(currentMonth); // Calculate start and end date
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Read search params on mount
    const params = new URLSearchParams(searchParams);
    const startDateParam = params.get("startDateTab3");
    const endDateParam = params.get("endDateTab3");
    const brandNameParam = params.get("brandNameTab3");

    if (startDateParam) setStartDate(startDateParam);
    if (endDateParam) setEndDate(endDateParam);
    if (brandNameParam) setBrandName(brandNameParam);

    // If startDate exists, calculate endDate based on period
    if (startDateParam && endDateParam) {
      setShow(true);
    } else {
      const currentMonth = dayjs().format("MM/YYYY");
      calculateStartAndEndDate(currentMonth);
    }
    // eslint-disable-next-line
  }, []); // Run only on mount

  const handleShow = () => {
    setShow(true);
    setDisplay(true);

    const updatedParams = new URLSearchParams(searchParams.toString());
    updatedParams.set("startDateTab3", startDate);
    updatedParams.set("endDateTab3", endDate);
    setSearchParams(updatedParams);
  };
  const filteredCompetitorListing = Array.from(new Set(competitorlisting));
  const headCells = Object.entries(tableHeadings);

  const handleExportCSV = () => {
    const csvRows = [];

    // Filter out the 'info' header if needed (or customize as necessary)
    const filteredHeadCells = headCells.filter(([key]) => key !== "info");

    // Extract the table headers
    const headers = filteredHeadCells.map(([_, value]) => value);
    csvRows.push(headers.join(","));

    // Extract the table data
    sortedRows.forEach((row: any) => {
      const rowData = filteredHeadCells.map(([key]) => {
        if (key === "total_cost") {
          return `"${(
            row.total_cost * row.qftd_actual || 0
          ).toLocaleString()}$"`;
        } else if (key === "utilization") {
          return `${Math.round(row.utilization * 100)}%`;
        } else if (key === "cost_per_qftd") {
          return row.cost_per_qftd !== null && row.cost_per_qftd
            ? `"${row.cost_per_qftd}$"`
            : "-";
        } else if (
          key === "brand_name" ||
          key === "modified_by" ||
          key === "info"
        ) {
          return row[key] || "N/A";
        } else if (key === "deposit_amount") {
          return row[key] ? `${row[key]} $` : "0";
        } else if (
          key === "date_of_aquisition" ||
          key === "campaign_id" ||
          key === "incentive_click_id" ||
          key === "optival_click_id"
        ) {
          return row[key] || "N/A";
        }
        return row[key] || "0";
      });

      csvRows.push(rowData.join(","));
    });

    // Convert CSV rows to a single string
    const csvString = csvRows.join("\n");

    // Create a blob and download the file
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "table_data_budget_control.csv");
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]} sx={{ py: 2 }}>
              <DatePicker
                slotProps={{
                  textField: {
                    size: "small",
                    color: "secondary",
                    required: true,
                  },
                }}
                sx={{ width: "100%" }}
                value={startDate ? dayjs(startDate) : null}
                onChange={(newValue) => {
                  if (newValue) {
                    const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
                    setStartDate(formattedDate);
                    setEndDate(null);
                  }
                }}
                label="Start Date"
                format="DD/MM/YY"
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]} sx={{ py: 2 }}>
              <DatePicker
                slotProps={{
                  textField: {
                    size: "small",
                    color: "secondary",
                    required: true,
                  },
                }}
                sx={{ width: "100%" }}
                label="End Date"
                disabled={!startDate}
                value={endDate ? dayjs(endDate) : null}
                minDate={startDate ? dayjs(startDate) : undefined}
                format="DD/MM/YY"
                onChange={(newValue) => {
                  if (newValue) {
                    const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
                    setEndDate(formattedDate);
                  }
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={5}>
          <Autocomplete
            sx={{ width: "100%", py: 2 }}
            options={filteredCompetitorListing}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search Brand or choose from the list"
                label="Competitor (optional)"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
              />
            )}
            onChange={(_, newValue: any) => {
              // Set the selected brand name into state
              setBrandName(newValue ? newValue : []); // or just newValue if it's already the desired format
              const updatedParams = new URLSearchParams(searchParams);
              if (newValue) {
                updatedParams.set("brandNameTab3", newValue);
              } else {
                updatedParams.delete("brandNameTab3"); // Remove param when cleared
                updatedParams.set("pageClickData", "0");
              }
              setSearchParams(updatedParams);
            }}
            value={brandName}
            size="small"
          />
        </Grid>
        <Grid
          item
          xs={3}
          textAlign={"right"}
          display="flex"
          justifyContent="flex-end"
        >
          <Button
            color="secondary"
            onClick={handleExportCSV}
            sx={{ marginRight: 2 }}
            data-tracker="190"
          >
            Export
          </Button>
          <Button
            variant="contained"
            color="secondary"
            sx={{ my: 2 }}
            onClick={handleShow}
            disabled={!endDate || !startDate}
            data-tracker="189"
          >
            show
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default ClickDataFilter;
